import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, Phone, MapPin, Bike, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, fmt, fmtTime, PAYMENTS, SOURCES, SOURCE_COLORS, STATUSES, todayStr } from "@/lib/api";
import { printReceipt } from "@/lib/print";
import { usePanel } from "./PanelContext";

const STATUS_STYLE = { yeni: "bg-[#FF6B0A] text-white", hazirlaniyor: "bg-amber-100 text-amber-800", yolda: "bg-sky-100 text-sky-800", teslim: "bg-emerald-100 text-emerald-800", iptal: "bg-red-100 text-red-700" };

function OrderCard({ o, couriers, onChange }) {
  const { api, biz } = usePanel();
  const patch = async (body, msg) => {
    try { await api.patch(`/orders/${o.id}`, body); if (msg) toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const closed = ["teslim", "iptal"].includes(o.status);
  return (
    <div className={`rounded-3xl bg-white border p-5 flex flex-col gap-3 ${o.status === "yeni" ? "border-[#FF6B0A] pulse-new" : ""}`} data-testid={`order-card-${o.no}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-display text-xl font-extrabold">#{o.no}</span>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${SOURCE_COLORS[o.source]}`}>{SOURCES[o.source]}</span>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[o.status]}`} data-testid={`order-status-${o.no}`}>{STATUSES[o.status]}</span>
        <span className="ml-auto text-xs text-slate-500">{fmtTime(o.created_at)}</span>
      </div>
      {o.table_name && <p className="text-sm font-semibold">{o.table_name}</p>}
      {(o.customer_name || o.customer_phone) && (
        <div className="text-sm space-y-1">
          <p className="font-semibold">{o.customer_name}</p>
          {o.customer_phone && <a href={`tel:${o.customer_phone}`} className="flex items-center gap-1.5 text-slate-600"><Phone className="h-3.5 w-3.5" />{o.customer_phone}</a>}
          {o.address && <p className="flex items-start gap-1.5 text-slate-600"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />{o.address}</p>}
        </div>
      )}
      <ul className="text-sm border-t border-dashed pt-2 space-y-0.5">
        {o.items.map((i, idx) => <li key={idx} className="flex justify-between"><span>{i.qty}x {i.name}</span><span className="font-mono-num">{fmt(i.price * i.qty)}</span></li>)}
      </ul>
      {o.note && <p className="text-xs bg-amber-50 rounded-lg p-2">Not: {o.note}</p>}
      <div className="flex items-center justify-between">
        <span className="font-display text-2xl font-extrabold">{fmt(o.total)}</span>
        <Select value={o.payment_method} onValueChange={(v) => patch({ payment_method: v })} disabled={closed}>
          <SelectTrigger className="w-36 h-9" data-testid={`order-payment-select-${o.no}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PAYMENTS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!closed && !o.table_id && (
        <div className="flex gap-2 items-center">
          <Bike className="h-4 w-4 text-slate-400" />
          <Select value={o.courier_id || ""} onValueChange={(v) => patch({ courier_id: v }, "Sipariş kuryeye gönderildi")}>
            <SelectTrigger className="h-9 flex-1" data-testid={`order-courier-select-${o.no}`}><SelectValue placeholder="Kuryeye gönder" /></SelectTrigger>
            <SelectContent>
              {couriers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`order-courier-option-${c.id}`}>{c.name}</SelectItem>)}
              {!couriers.length && <div className="text-xs p-2 text-slate-500">Önce kurye ekleyin</div>}
            </SelectContent>
          </Select>
        </div>
      )}
      {o.courier_name && <p className="text-xs text-sky-700 font-semibold" data-testid={`order-courier-name-${o.no}`}>Kurye: {o.courier_name}</p>}
      <div className="flex flex-wrap gap-2 pt-1">
        {o.status === "yeni" && <Button size="sm" className="rounded-full bg-amber-500 hover:bg-amber-600" onClick={() => patch({ status: "hazirlaniyor" })} data-testid={`order-prepare-btn-${o.no}`}>Hazırlanıyor</Button>}
        {["yeni", "hazirlaniyor"].includes(o.status) && !o.table_id && <Button size="sm" className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => patch({ status: "yolda" })} data-testid={`order-onway-btn-${o.no}`}>Yolda</Button>}
        {!closed && <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => patch({ status: "teslim" }, "Teslim edildi")} data-testid={`order-deliver-btn-${o.no}`}>Teslim Edildi</Button>}
        <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => { printReceipt(o, biz); patch({ printed: true }); }} data-testid={`order-print-btn-${o.no}`}><Printer className="h-3.5 w-3.5" />Fiş</Button>
        {!closed && <Button size="sm" variant="ghost" className="rounded-full text-red-600" onClick={() => window.confirm("Sipariş iptal edilsin mi?") && patch({ status: "iptal" }, "İptal edildi")} data-testid={`order-cancel-btn-${o.no}`}>İptal</Button>}
      </div>
    </div>
  );
}

export default function Orders() {
  const { api, activeOrders, refreshOrders, openNewOrder } = usePanel();
  const [tab, setTab] = useState("aktif");
  const [date, setDate] = useState(todayStr());
  const [history, setHistory] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/couriers").then((r) => setCouriers(r.data)).catch(() => {}); }, [api]);
  const loadHistory = () => api.get("/orders", { params: { start: date, end: date } }).then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { if (tab === "gecmis") loadHistory(); }, [tab, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = (tab === "aktif" ? activeOrders : history).filter((o) => filter === "all" || o.source === filter);
  const onChange = () => { refreshOrders(); if (tab === "gecmis") loadHistory(); };

  return (
    <div className="space-y-6 rise" data-testid="orders-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto"><h1 className="text-3xl font-extrabold">Siparişler</h1><p className="text-sm text-slate-500">Yeni siparişler her 5 saniyede otomatik gelir, sesli uyarı çalar.</p></div>
        <Button className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] gap-1.5" onClick={() => openNewOrder()} data-testid="orders-new-btn"><Plus className="h-4 w-4" />Yeni Sipariş</Button>
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="aktif" data-testid="orders-tab-active">Aktif <Badge className="ml-2 bg-[#FF6B0A]">{activeOrders.length}</Badge></TabsTrigger>
            <TabsTrigger value="gecmis" data-testid="orders-tab-history">Geçmiş</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "gecmis" && <Input type="date" className="w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="orders-date-input" />}
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="orders-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm kaynaklar</SelectItem>
            {Object.entries(SOURCES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid md:grid-cols-2 2xl:grid-cols-3 gap-4">
        {list.map((o) => <OrderCard key={o.id} o={o} couriers={couriers} onChange={onChange} />)}
      </div>
      {!list.length && <div className="rounded-3xl border border-dashed p-12 text-center text-slate-500" data-testid="orders-empty">Sipariş bulunmuyor.</div>}
    </div>
  );
}
